import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';

import Button from 'WeployDesignLanguage/buttons/Button';

import directIllustration from '../../../components/weployer/jobTypeSelectionModal/direct_illustration.svg';
import selectllustration from '../../../components/weployer/jobTypeSelectionModal/select_illustration.svg';
import rehireIllustration from '../../../components/weployer/jobTypeSelectionModal/rehire_illustration.svg';

import { GET_HAS_PAST_WEPLOYEE_WITH_SUBMITTED_TIMESHEET } from './graphql/queries';

import { JOB_TYPES } from './JobTypeSelectionModal';

const StyledWrapper = styled.div`
  width: 100%;
  position: relative;
  height: 100%;
`;

const StyledInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80%;
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledJobTypeOptions = styled.div`
  display: flex;
`;

const StyledJobPostingOption = styled.div`
  max-width: 300px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  &:not(:last-child) {
    margin-right: ${props => props.theme.spacing[5]};
  }
`;

const StyledJobPostingOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled.h5`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledOptionTitle = styled.h5``;

const StyledDescription = styled.p`
  color: ${props => props.theme.colors.text.primary};
  text-align: center;
`;

const StyledOptionIllustrationContainer = styled.div``;

const StyledOptionIllustration = styled.img`
  max-width: 100%;
  margin-bottom: ${props => props.theme.spacing[3]};
  filter: ${({ disabled, src }) => (disabled && src === rehireIllustration ? 'grayscale(1)' : 'none')}
`;

const StyledCaption = styled.span`
  ${props => props.theme.typography.caption};
  text-align: center;
`;

const jobTypes = () => [
  {
    jobType: JOB_TYPES.DIRECT,
    image: directIllustration,
    description: 'The Weploy algorithm matches you automatically to a qualified Weployee',
  },
  {
    jobType: JOB_TYPES.SELECT,
    image: selectllustration,
    description: 'Multiple Weployees can express interest in your role, and you must make a selection within 24 hours',
  },
  {
    jobType: JOB_TYPES.REHIRE,
    image: rehireIllustration,
    description: 'Select Weployees from a customised pool based on people that have worked with you in the past. Make a selection within 24 hours',
  },
];

const renderButtonOrUnavailableMessage = (jobType, hasPastWeployees, onContinueClick) => (
  jobType === JOB_TYPES.REHIRE && !hasPastWeployees
    ? <StyledCaption>Hire your first Weployees through Select or Direct to unlock this feature</StyledCaption>
    : <Button onClick={() => onContinueClick(jobType)} >Continue</Button>
);

const JobTypeOptions = ({ onContinueClick }) => {
  const { data, loading } = useQuery(GET_HAS_PAST_WEPLOYEE_WITH_SUBMITTED_TIMESHEET);
  const hasPastWeployees = get(data, 'weployer.hasPastWeployeeWithSubmittedTimesheet');

  return (
    <Loader loading={loading}>
      <StyledWrapper>
        <StyledInnerWrapper>
          <StyledTitle>Choose the sourcing strategy that matches your needs</StyledTitle>
          <StyledJobTypeOptions>
            {
              jobTypes().map(({ jobType, image, description }) => (
                <StyledJobPostingOption key={jobType}>
                  <StyledJobPostingOptionContent>
                    <StyledOptionIllustrationContainer>
                      <StyledOptionIllustration src={image} alt="Job Posting Option Illustration" disabled={!hasPastWeployees} />
                    </StyledOptionIllustrationContainer>
                    <StyledOptionTitle>{jobType}</StyledOptionTitle>
                    <StyledDescription>{description}</StyledDescription>
                  </StyledJobPostingOptionContent>
                  {renderButtonOrUnavailableMessage(jobType, hasPastWeployees, onContinueClick)}
                </StyledJobPostingOption>
              ))
            }
          </StyledJobTypeOptions>
        </StyledInnerWrapper>
      </StyledWrapper>
    </Loader>
  );
};

JobTypeOptions.propTypes = {
  onContinueClick: PropTypes.func.isRequired,
};

export default JobTypeOptions;
